// Il repository del progetto: dall'elenco del server (Gitea, GitHub), oppure creato da PopTown con
// «Crea nuovo». Un git generico non ha elenco: si scrive proprietario/nome.
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import type { GitRegistry, GitRepo } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { TextField } from '../../components/Field';
import { Select } from '../../components/Select';
import { Switch } from '../../components/Switch';

const NEW = '__new';

export function RepoPicker({
  registry,
  repos,
  value,
  onChange,
}: {
  registry: GitRegistry;
  repos: GitRepo[] | null;
  value: string;
  onChange: (repo: string, defaultBranch?: string) => void;
}) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState(`${registry.username}/`);
  const [secret, setSecret] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // senza elenco (git generico, o server che non ne dà) il nome si scrive a mano
  if (!creating && (registry.kind === 'git' || repos?.length === 0))
    return (
      <div style={{ display: 'grid', gap: 8 }}>
        <TextField mono value={value} onChange={(v) => onChange(v)} placeholder="Labs/PopTown" />
        {registry.kind !== 'git' && (
          <Button size="sm" icon="plus" onClick={() => setCreating(true)}>
            Crea nuovo
          </Button>
        )}
      </div>
    );

  if (creating)
    return (
      <div style={{ display: 'grid', gap: 8 }}>
        <TextField mono value={name} onChange={setName} autoFocus />
        <Switch checked={secret} onChange={setSecret} label="Privato" />
        {error && <Banner kind="danger">{error}</Banner>}
        <div style={{ display: 'flex', gap: 8 }}>
          <Button size="sm" variant="ghost" onClick={() => setCreating(false)}>
            Annulla
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={busy}
            disabled={!/^[^/\s]+\/[^/\s]+$/.test(name.trim())}
            onClick={() => {
              setBusy(true);
              setError(null);
              api.createGitRepo(registry.id, name.trim(), secret).then(
                (r) => {
                  onChange(r.fullName, r.defaultBranch);
                  setCreating(false);
                  setBusy(false);
                },
                (e: unknown) => {
                  setError(e instanceof ApiError && e.detail ? e.detail : 'Non riesco a crearlo.');
                  setBusy(false);
                },
              );
            }}
          >
            Crea
          </Button>
        </div>
      </div>
    );

  const known = repos ?? [];
  // raggruppati per organizzazione, in ordine (richiesta del 2026-11-01): l'intestazione è il
  // proprietario, la voce il nome del repository
  const split = (full: string) => {
    const at = full.indexOf('/');
    return at > 0 ? [full.slice(0, at), full.slice(at + 1)] : ['', full];
  };
  const order = (x: string, y: string) => x.localeCompare(y, 'it', { sensitivity: 'base' });
  const sorted = [...known].sort((a, b) => {
    const [ao, an] = split(a.fullName);
    const [bo, bn] = split(b.fullName);
    return order(ao, bo) || order(an, bn);
  });
  const options = [
    { value: NEW, label: 'Crea nuovo…' },
    ...(value && !known.some((r) => r.fullName === value) ? [{ value, label: value }] : []),
    ...sorted.map((r) => {
      const [owner, name] = split(r.fullName);
      return {
        value: r.fullName,
        group: owner || undefined,
        label: `${name}${r.private ? ' · privato' : ''}`,
        selectedLabel: `${r.fullName}${r.private ? ' · privato' : ''}`,
        disabled: !r.canPush,
        reason: r.canPush ? undefined : 'Il token non può fare push qui',
      };
    }),
  ];
  return (
    <Select
      label="Repository"
      value={value}
      onChange={(v) => {
        if (v === NEW) setCreating(true);
        else onChange(v, known.find((r) => r.fullName === v)?.defaultBranch);
      }}
      options={repos === null ? [{ value: '', label: 'Carico i repository…' }] : options}
      width="100%"
    />
  );
}
